import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useData } from '../data/DataContext';
import { useToast } from '../contexts/ToastContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import StatCard from '../components/ui/StatCard';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import PaymentFormModal from '../components/payments/PaymentFormModal';
import PaymentDetailModal from '../components/payments/PaymentDetailModal';
import { formatCurrency, formatDate, formatNumber, payerLabel } from '../utils/format';
import useDebounce from '../hooks/useDebounce';

const TYPES = ['cotisation', 'pelerinage', 'offrande'];
const STATUSES = ['paid', 'pending', 'cancelled', 'refunded'];
const TYPE_TONE = { cotisation: 'cotisation', pelerinage: 'pelerinage', offrande: 'offrande' };
const STATUS_TONE = { paid: 'paid', pending: 'pending', cancelled: 'inactive', refunded: 'completed' };

export default function PaymentsPage() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { payments, createPayment, updatePayment, deletePayment, memberById, eventById } = useData();
  const [params, setParams] = useSearchParams();

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const debouncedQuery = useDebounce(query, 200);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (params.get('new') === '1') {
      setEditing(null);
      setFormOpen(true);
      params.delete('new');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const stats = useMemo(() => {
    const paid = payments.filter((p) => p.status === 'paid');
    const total = paid.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
    const pendingCount = payments.filter((p) => p.status === 'pending').length;
    const now = new Date();
    const monthTotal = paid
      .filter((p) => {
        const d = new Date(p.date);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
    return { total, pendingCount, monthTotal };
  }, [payments]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return payments
      .filter((p) => {
        if (typeFilter && p.type !== typeFilter) return false;
        if (statusFilter && p.status !== statusFilter) return false;
        if (!q) return true;
        const haystack = [
          p.code || p.id || '',
          payerLabel(p, memberById),
          p.payerName || '',
          p.externalRef || '',
          p.method,
        ].join(' ').toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [payments, debouncedQuery, typeFilter, statusFilter, memberById]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setEditing(p); setFormOpen(true); };
  const openView = (p) => { setViewing(p); };
  const askDelete = (p) => { setDeleting(p); };

  const handleSubmit = async (data) => {
    if (editing) {
      updatePayment(editing.id, data);
      toast.success(t('payments.toast_updated', { ref: editing.code || editing.id }));
    } else {
      const next = createPayment(data);
      toast.success(t('payments.toast_created', { ref: next.code || next.id }));
    }
    setFormOpen(false);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    deletePayment(deleting.id);
    toast.success(t('payments.toast_deleted'));
    setDeleting(null);
  };

  const handleMarkPaid = (p) => {
    updatePayment(p.id, { status: 'paid' });
    toast.success(t('payments.toast_marked_paid', { ref: p.code || p.id }));
    setViewing((prev) => (prev && prev.id === p.id ? { ...prev, status: 'paid' } : prev));
  };

  const typeOptions = useMemo(
    () => [
      { value: '', label: t('payments.filter_type') },
      ...TYPES.map((v) => ({ value: v, label: t(`payments.types.${v}`) })),
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('payments.filter_status') },
      ...STATUSES.map((v) => ({ value: v, label: t(`payments.statuses.${v}`) })),
    ],
    [t]
  );

  const columns = [
    {
      key: 'id',
      header: t('common.reference'),
      cellClassName: 'mono',
      render: (r) => (r.externalRef ? (
        <div className="cell-stack">
          <span>{r.code || r.id}</span>
          <span className="cell-sub" title={t('payments.field_external_ref')}>↳ {r.externalRef}</span>
        </div>
      ) : (r.code || r.id)),
    },
    {
      key: 'member',
      header: t('payments.field_payer'),
      render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          {payerLabel(r, memberById)}
          {!r.memberId && r.payerName && (
            <Badge tone="pending">{t('payments.payer_guest_label')}</Badge>
          )}
        </span>
      ),
    },
    { key: 'type', header: t('payments.field_type'), render: (r) => <Badge tone={TYPE_TONE[r.type]}>{t(`payments.types.${r.type}`)}</Badge> },
    { key: 'amount', header: t('payments.field_amount'), render: (r) => <strong>{formatCurrency(r.amount, locale)}</strong> },
    { key: 'date', header: t('payments.field_date'), render: (r) => formatDate(r.date, t) },
    { key: 'method', header: t('payments.field_method'), render: (r) => t(`payments.methods.${r.method}`) },
    {
      key: 'event',
      header: t('payments.field_event'),
      render: (r) => {
        if (!r.eventId) return '—';
        const ev = eventById(r.eventId);
        return ev ? ev.name : '—';
      },
    },
    { key: 'status', header: t('common.status'), render: (r) => <Badge tone={STATUS_TONE[r.status]}>{t(`payments.statuses.${r.status}`)}</Badge> },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => openView(r)}>{t('common.view')}</button>
          <button type="button" className="action-btn" onClick={() => openEdit(r)}>{t('common.edit')}</button>
          <button type="button" className="action-btn danger" onClick={() => askDelete(r)}>{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <StatCard
          label={t('payments.total_collected')}
          value={formatNumber(stats.total, locale)}
          change={t('payments.total_all_time')}
          icon="💰"
          tone="sc4"
        />
        <StatCard
          label={t('payments.pending')}
          value={stats.pendingCount}
          change={t('payments.pending_caption')}
          changeTone="warn"
          icon="⏳"
          tone="sc2"
        />
        <StatCard
          label={t('payments.this_month')}
          value={formatNumber(stats.monthTotal, locale)}
          change={t('payments.this_month_caption')}
          icon="📅"
          tone="sc1"
        />
      </div>

      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('payments.search_placeholder')} />
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} options={typeOptions} className="filter-select" />
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={statusOptions} className="filter-select" />
        <Button variant="ghost" onClick={() => toast.info(t('toast.not_implemented'))}>{t('common.export')}</Button>
        <Button variant="gold" onClick={openNew}>+ {t('payments.record_payment')}</Button>
      </SearchRow>

      <Card title={t('payments.title')} subtitle={t('payments.subtitle')} padded={false}>
        <DataTable
          columns={columns}
          rows={filtered}
          empty={(
            <EmptyState
              title={t('payments.empty_title')}
              body={t('payments.empty_body')}
              action={<Button variant="gold" onClick={openNew}>+ {t('payments.record_payment')}</Button>}
            />
          )}
        />
      </Card>

      <PaymentFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
      />

      <PaymentDetailModal
        isOpen={Boolean(viewing)}
        onClose={() => setViewing(null)}
        payment={viewing}
        onEdit={() => { setEditing(viewing); setViewing(null); setFormOpen(true); }}
        onMarkPaid={viewing ? () => handleMarkPaid(viewing) : undefined}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={t('payments.delete_confirm_title')}
        body={deleting ? t('payments.delete_confirm_body', { ref: deleting.code || deleting.id }) : ''}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
